import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import MfaSetup from './MfaSetup';

const Container = styled.div`
  background: linear-gradient(135deg, #1a1f35 0%, #0d1117 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  color: #fff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span<{ $enabled: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => props.$enabled ? `
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  ` : `
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  `}
`;

const Description = styled.p`
  color: #a0aec0;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
  padding: 1rem;
`;

const InfoLabel = styled.div`
  color: #64748b;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    if (props.variant === 'danger') {
      return `
        background: rgba(239, 68, 68, 0.2);
        border: 1px solid rgba(239, 68, 68, 0.5);
        color: #ef4444;
        
        &:hover {
          background: rgba(239, 68, 68, 0.3);
        }
      `;
    }
    if (props.variant === 'secondary') {
      return `
        background: transparent;
        border: 1px solid rgba(139, 92, 246, 0.5);
        color: #8b5cf6;
        
        &:hover {
          background: rgba(139, 92, 246, 0.1);
        }
      `;
    }
    return `
      background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
      color: white;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin: 0 0 1rem 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

interface MfaStatus {
  mfaEnabled: boolean;
  mfaEnabledAt: string | null;
  backupCodesRemaining: number;
}

const MfaSettings: React.FC = () => {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [disabling, setDisabling] = useState(false);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch MFA status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetupComplete = () => {
    setShowSetup(false);
    fetchStatus();
  };

  const handleDisableMfa = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setDisabling(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to disable MFA');
      }
      
      setShowDisableModal(false);
      setPassword('');
      fetchStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Title>🔐 Two-Factor Authentication</Title>
        <Description>Loading...</Description>
      </Container>
    );
  }

  if (showSetup) {
    return (
      <MfaSetup 
        onComplete={handleSetupComplete}
        onCancel={() => setShowSetup(false)}
      />
    );
  }

  return (
    <>
      <Container>
        <Header>
          <Title>🔐 Two-Factor Authentication</Title>
          <StatusBadge $enabled={status?.mfaEnabled || false}>
            {status?.mfaEnabled ? 'Enabled' : 'Disabled'}
          </StatusBadge>
        </Header>
        
        <Description>
          {status?.mfaEnabled
            ? 'Your account is protected with two-factor authentication. You\'ll need your authenticator app to sign in.'
            : 'Add an extra layer of security to your account by requiring a verification code from your authenticator app when signing in.'
          }
        </Description>
        
        {status?.mfaEnabled && (
          <InfoGrid>
            <InfoCard>
              <InfoLabel>Enabled Since</InfoLabel>
              <InfoValue>
                {status.mfaEnabledAt
                  ? new Date(status.mfaEnabledAt).toLocaleDateString()
                  : 'Unknown'
                }
              </InfoValue>
            </InfoCard>
            <InfoCard>
              <InfoLabel>Backup Codes Left</InfoLabel>
              <InfoValue>{status.backupCodesRemaining} of 8</InfoValue>
            </InfoCard>
          </InfoGrid>
        )}
        
        <ButtonGroup>
          {status?.mfaEnabled ? (
            <>
              <Button variant="secondary" onClick={() => setShowSetup(true)}>
                Regenerate Backup Codes
              </Button>
              <Button variant="danger" onClick={() => setShowDisableModal(true)}>
                Disable MFA
              </Button>
            </>
          ) : (
            <Button onClick={() => setShowSetup(true)}>
              Enable Two-Factor Authentication
            </Button>
          )}
        </ButtonGroup>
      </Container>
      
      {showDisableModal && (
        <Modal onClick={() => setShowDisableModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>⚠️ Disable Two-Factor Authentication</ModalTitle>
            <Description>
              This will make your account less secure. Enter your password to confirm.
            </Description>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoFocus
            />
            
            <ButtonGroup>
              <Button variant="danger" onClick={handleDisableMfa} disabled={disabling}>
                {disabling ? 'Disabling...' : 'Disable MFA'}
              </Button>
              <Button variant="secondary" onClick={() => {
                setShowDisableModal(false);
                setPassword('');
                setError('');
              }}>
                Cancel
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default MfaSettings;
